import React, { useState } from 'react';

interface ConnectorRecord {
  id: string;
  name: string;
  addresses: string[];
  updateTime: string;
}

interface ConnectorModalState {
  mode: 'create' | 'edit';
  name: string;
  addresses: string;
}

const formatNow = () => new Date().toLocaleString('zh-CN', { hour12: false });
const namePattern = /^[A-Za-z0-9_-]+$/;
const splitAddresses = (value: string) =>
  value
    .split(/[\n,，;；]+/g)
    .map(item => item.trim())
    .filter(Boolean);

const CloudMeetingConnector: React.FC = () => {
  const [connector, setConnector] = useState<ConnectorRecord | null>(null);
  const [modal, setModal] = useState<ConnectorModalState | null>(null);
  const [formError, setFormError] = useState('');

  const handleOpenCreateModal = () => {
    setModal({ mode: 'create', name: '', addresses: '' });
    setFormError('');
  };

  const handleOpenEditModal = () => {
    if (!connector) {
      return;
    }
    setModal({
      mode: 'edit',
      name: connector.name,
      addresses: connector.addresses.join('\n')
    });
    setFormError('');
  };

  const handleCloseModal = () => {
    setModal(null);
    setFormError('');
  };

  const handleFormChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setModal(prev => (prev ? { ...prev, [name]: value } : prev));
    if (formError) {
      setFormError('');
    }
  };

  const handleSave = (event: React.FormEvent) => {
    event.preventDefault();
    if (!modal) {
      return;
    }

    const name = modal.name.trim();
    const addresses = splitAddresses(modal.addresses);

    if (!name || addresses.length === 0) {
      setFormError('云会议连接器名称和地址不能为空');
      return;
    }
    if (!namePattern.test(name)) {
      setFormError('名称仅支持英文、数字、下划线和中划线');
      return;
    }

    setConnector({
      id: connector?.id || `connector-${Date.now()}`,
      name,
      addresses,
      updateTime: formatNow()
    });
    handleCloseModal();
  };

  const handleDelete = () => {
    setConnector(null);
  };

  return (
    <div className="page-block">
      <div className="breadcrumb">云会议 &gt; 云会议连接器</div>

      <div className="page-card">
        <div className="toolbar toolbar-bottom">
          <div className="toolbar-left">
            <span className="meta-text">云会议连接器仅支持配置 1 条</span>
          </div>
          <div className="toolbar-right">
            <button
              type="button"
              className={`btn ${connector ? 'btn-secondary' : 'btn-primary'}`}
              onClick={handleOpenCreateModal}
              disabled={Boolean(connector)}
            >
              新建
            </button>
          </div>
        </div>

        <div className="table-container sip-connector-table-container">
          <table className="compact-table sip-connector-table">
            <thead>
              <tr>
                <th>名称</th>
                <th>地址</th>
                <th>更新时间</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {connector ? (
                <tr>
                  <td>{connector.name}</td>
                  <td>
                    <div className="connector-address-list">
                      {connector.addresses.map(address => (
                        <div key={address}>{address}</div>
                      ))}
                    </div>
                  </td>
                  <td>{connector.updateTime}</td>
                  <td>
                    <div className="table-actions">
                      <button type="button" className="text-link" onClick={handleOpenEditModal}>
                        修改
                      </button>
                      <button type="button" className="text-link danger" onClick={handleDelete}>
                        删除
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                <tr>
                  <td colSpan={4} className="table-empty">暂无云会议连接器，请先新建</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {modal && (
        <div className="modal-backdrop" onClick={handleCloseModal}>
          <div className="modal sip-rename-modal" onClick={event => event.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h3>{modal.mode === 'create' ? '新建云会议连接器' : '修改云会议连接器'}</h3>
              </div>
              <button
                type="button"
                className="modal-close-btn"
                aria-label="关闭"
                onClick={handleCloseModal}
              >
                <span className="modal-close-icon" aria-hidden="true" />
              </button>
            </div>
            <form className="form-grid sip-connector-form" onSubmit={handleSave}>
              <label className="form-field">
                <span>名称</span>
                <input
                  name="name"
                  value={modal.name}
                  onChange={handleFormChange}
                  placeholder="请输入云会议连接器名称"
                />
              </label>
              <label className="form-field">
                <span>地址</span>
                <textarea
                  name="addresses"
                  value={modal.addresses}
                  onChange={handleFormChange}
                  placeholder="请输入云会议连接器地址，支持换行或逗号分隔多个地址"
                />
              </label>
              <div className="form-actions">
                {formError && <span className="field-error">{formError}</span>}
                <button type="submit" className="btn btn-primary">保存</button>
                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>取消</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CloudMeetingConnector;
